<template>
  <div class="flex min-w-0 flex-col gap-0.5">
    <span class="text-sm text-text-muted">
      {{ $t('W: Rapportsprog: {language}', { language: capitalizedLanguageName }) }}
    </span>
    <span
      v-if="itemsMissingTranslation > 0"
      class="text-xs text-feedback-warning">
      {{ missingText }}
    </span>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { mapState } from 'pinia'
import {
  getCapitalizedLanguageName,
  getLanguageName,
  getReportLocaleIssue,
} from '@/globals/typescript/utils/reportLocaleCoverage'
import { useAppStore } from '@/stores/appStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'

/**
 * The language the report is printed in (named in the user's own language), and — when some of the
 * chosen descriptions have no text in it — how many, since the report then prints those in another
 * language. The modals themselves stay in the user's language.
 */
export default {
  name: 'ReportLanguageNotice',

  props: {
    reportLocale: { type: String, required: true },
    /** The chosen descriptions — every one the report will print. */
    itemIDs: { type: Array as PropType<string[]>, required: true },
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useReportDescriptionItemStore, ['allReportDescriptionItems']),

    capitalizedLanguageName(): string {
      return getCapitalizedLanguageName(this.reportLocale, this.currentLocale)
    },

    itemsMissingTranslation(): number {
      return [...new Set(this.itemIDs)].filter((itemID) => {
        const item = this.allReportDescriptionItems.find((x) => x.id === itemID)
        return !!item && getReportLocaleIssue(item, this.reportLocale) !== null
      }).length
    },

    missingText(): string {
      const language = getLanguageName(this.reportLocale, this.currentLocale)
      return this.itemsMissingTranslation === 1
        ? this.$t('W: 1 valgt beskrivelse mangler oversættelse til {language}', { language })
        : this.$t('W: {count} valgte beskrivelser mangler oversættelse til {language}', {
            count: this.itemsMissingTranslation,
            language,
          })
    },
  },
}
</script>
